import { z } from 'zod';
import { VIRGINIA_PLACE_OUTLINE_SQUARES, VIRGINIA_PLACE_OUTLINES_SQUARE_TENTHS } from './virginia-place-outlines.generated.js';
import { VIRGINIA_BOX, findVirginiaPlace, virginiaPlaceCodeSchema } from './virginia-places.js';
export { VIRGINIA_PLACE_CODES_WITHOUT_OUTLINE, VIRGINIA_PLACE_OUTLINES_PLACES_YEAR, VIRGINIA_PLACE_OUTLINES_TOLERANCE_METRES, VIRGINIA_PLACE_OUTLINES_ZCTA_YEAR, } from './virginia-place-outlines.generated.js';
/**
 * The outline of each place in the app's own list of Virginia places (ADR-021, #615), so that the Roster's map can
 * shade the area a place covers.
 *
 * Built from US Census Bureau boundary files by `scripts/build-virginia-place-outlines.mjs`.
 * `VIRGINIA-PLACE-OUTLINES-SOURCE.md` beside this file says where the files came from, what may be done with them and
 * how much the outlines were thinned. Nothing here is about a person.
 *
 * LOADING. The outlines are far larger than the list, so they are NOT part of this module. They are filed by
 * half-degree square of the map, one generated file a square, and each file is reached only through `import()`. In
 * the web build each becomes a separate small file on our own origin, fetched the first time an outline in that
 * square is asked for, and kept by the browser after that. A Roster asks for the handful of places that have staff
 * and so fetches only the squares those places are in.
 *
 * Framework-free: no React, no Leaflet, no Azure, no clock.
 */
const longitudeSchema = z.number().min(VIRGINIA_BOX.west).max(VIRGINIA_BOX.east);
const latitudeSchema = z.number().min(VIRGINIA_BOX.south).max(VIRGINIA_BOX.north);
/**
 * One corner: `[longitude, latitude]` in degrees, EAST FIRST, as GeoJSON has it. Leaflet's own calls take the
 * latitude first; its GeoJSON layer takes this order as it is.
 */
export const virginiaPlaceOutlinePositionSchema = z.tuple([longitudeSchema, latitudeSchema]);
/** One closed line: four corners or more, the last one the same as the first. */
export const virginiaPlaceOutlineRingSchema = z.array(virginiaPlaceOutlinePositionSchema).min(4).superRefine((ring, ctx) => {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (!first || !last || first[0] !== last[0] || first[1] !== last[1])
        ctx.addIssue({ code: 'custom', message: 'The ring is not closed' });
});
/**
 * The outline of one listed place. `polygons` is exactly the `coordinates` of a GeoJSON `MultiPolygon`: a place is one
 * or more separate parts (the largest first); each part is its outer ring followed by any holes in it. The outer ring
 * runs counter-clockwise and a hole clockwise.
 */
export const virginiaPlaceOutlineSchema = z.object({
    code: virginiaPlaceCodeSchema,
    polygons: z.array(z.array(virginiaPlaceOutlineRingSchema).min(1)).min(1),
}).strict();
/**
 * The square of the map a listed place's outline is filed under, or `undefined` for a code that is not in the list.
 * It is the square the place's listed position falls in, named by its south-west corner in tenths of a degree:
 * "n385w775" is 38.5 north, 77.5 west. The build script works out the same name from the same position.
 */
export function virginiaPlaceOutlineSquare(code) {
    const place = findVirginiaPlace(code);
    if (!place)
        return undefined;
    const south = Math.floor((place.lat * 10) / VIRGINIA_PLACE_OUTLINES_SQUARE_TENTHS) * VIRGINIA_PLACE_OUTLINES_SQUARE_TENTHS;
    const west = Math.ceil((-place.lng * 10) / VIRGINIA_PLACE_OUTLINES_SQUARE_TENTHS) * VIRGINIA_PLACE_OUTLINES_SQUARE_TENTHS;
    return `n${south}w${west}`;
}
/** The 64 characters a packed number is written in, and how many units make one degree. Both are in the build script. */
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const SCALE = 1e5;
/**
 * A packed outline, unpacked. Polygons are joined by `;` and rings by `,`. A ring is its corners without the closing
 * one; a corner is two whole numbers, the step east and the step north from the corner before it (the first corner of
 * a place steps from zero), in hundred-thousandths of a degree. A number is one to six characters of `ALPHABET`, five
 * bits each with the low bits first; a character worth 32 or more says another follows; the lowest bit of the number
 * is its sign.
 */
function unpack(code, packed) {
    let x = 0;
    let y = 0;
    const polygons = packed.split(';').map((polygon) => polygon.split(',').map((ring) => {
        const corners = [];
        let rest = 0;
        let shift = 0;
        let east;
        for (const character of ring) {
            const digit = ALPHABET.indexOf(character);
            rest += (digit % 32) * 2 ** shift;
            shift += 5;
            if (digit >= 32)
                continue;
            const step = rest % 2 === 1 ? -(rest + 1) / 2 : rest / 2;
            rest = 0;
            shift = 0;
            if (east === undefined) {
                east = step;
                continue;
            }
            x += east;
            y += step;
            east = undefined;
            corners.push([x / SCALE, y / SCALE]);
        }
        const first = corners[0];
        if (first)
            corners.push([first[0], first[1]]);
        return corners;
    }));
    return { code, polygons };
}
/** The rows of each square asked for so far, by place code. A row is kept packed; unpacking is done for each answer. */
const squares = new Map();
const NOTHING = new Map();
function rowsOfSquare(name) {
    const known = squares.get(name);
    if (known)
        return known;
    const load = VIRGINIA_PLACE_OUTLINE_SQUARES[name];
    if (!load)
        return Promise.resolve(NOTHING);
    const rows = load().then(({ ROWS }) => new Map(ROWS.split('\n').filter((line) => line !== '').map((line) => {
        const bar = line.indexOf('|');
        return [line.slice(0, bar), line.slice(bar + 1)];
    })));
    squares.set(name, rows);
    // A square that could not be fetched is forgotten, so the next call asks for it again.
    rows.catch(() => squares.delete(name));
    return rows;
}
/**
 * The outlines of these places, by place code. THE ONE CALL THE MAP NEEDS: give it the codes of the places on the
 * Roster and it fetches only the squares those places are in (nothing at all the second time).
 *
 * - A code with no outline is simply not in the answer: a code that is not in the list (a Google place id, a typing
 *   mistake), or a listed place the Census files have no outline for. The map shows such a place as a pin.
 * - Each answer is a fresh object the caller owns. The same code twice gives one entry.
 * - It rejects only when a square could not be fetched (the network, a new release mid-session). Nothing is
 *   remembered of a failed fetch, so calling again tries again. The map falls back to pins meanwhile.
 */
export async function loadVirginiaPlaceOutlines(codes) {
    const wanted = new Map();
    for (const code of codes) {
        const square = virginiaPlaceOutlineSquare(code);
        if (square !== undefined)
            wanted.set(code, square);
    }
    const names = [...new Set(wanted.values())];
    const fetched = new Map(await Promise.all(names.map(async (name) => [name, await rowsOfSquare(name)])));
    const found = new Map();
    for (const [code, square] of wanted) {
        const packed = fetched.get(square)?.get(code);
        if (packed !== undefined)
            found.set(code, unpack(code, packed));
    }
    return found;
}
/** The outline of one place, or `undefined` when it has none. The lookup; `loadVirginiaPlaceOutlines` is the same for several. */
export async function loadVirginiaPlaceOutline(code) {
    return (await loadVirginiaPlaceOutlines([code])).get(code);
}
//# sourceMappingURL=virginia-place-outlines.js.map